import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { COMPANY_CONFIGS, CompanyType } from '../../mocks/companyTypes';
import { useRole } from '../../contexts/RoleContext';

const COMPANY_ORDER: CompanyType[] = [
  'MusicLabel',
  'LiveEntertainment',
  'CulturalFoundation',
  'WebtoonStudio',
  'ContentAgency',
];

export default function CompanySelectPage() {
  const navigate = useNavigate();
  const { setCompanyType } = useRole();
  const [selected, setSelected] = useState<CompanyType | null>(null);
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    if (!selected) return;
    setLoading(true);
    await setCompanyType(selected);
    localStorage.setItem('kap_company_type', selected);
    navigate('/role-select');
  };

  return (
    <div className="min-h-screen bg-[#0C0C0E] flex flex-col items-center justify-center px-4 sm:px-8 py-10 md:py-12">
      {/* Header */}
      <div className="text-center mb-8 md:mb-10 max-w-xl w-full">
        <div className="flex items-center justify-center gap-2.5 mb-6 md:mb-8">
          <img
            src="https://storage.readdy-site.link/project_files/e81f0f74-7f28-46c8-8485-3df778381a50/9a17726c-ea2b-4ef1-b154-44e1a8dd8821_Gemini_Generated_Image_at6qf4at6qf4at6q-removebg-preview.png?v=7e9fc5a9989fd2f2c3e90b758ae037ba"
            alt="K-ArtPEN"
            className="w-8 h-8 rounded-lg object-contain opacity-80"
          />
          <span className="text-[#F2F2F2] font-semibold text-sm tracking-widest">K-ArtPEN</span>
        </div>
        <h1 className="text-[#F2F2F2] text-xl md:text-2xl font-semibold tracking-tight mb-2 md:mb-3">
          어떤 기업을 운영하시겠습니까?
        </h1>
        <p className="text-[#666666] text-sm leading-relaxed">
          기업 유형에 따라 직무, 업무 화면, KPI가 모두 달라집니다.
        </p>
      </div>

      {/* Company Cards — 모바일: 1열, sm: 2열, lg: 3열, xl: 5열 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 w-full max-w-5xl mb-6 md:mb-8">
        {COMPANY_ORDER.map((key) => {
          const company = COMPANY_CONFIGS[key];
          const isSelected = selected === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setSelected(key)}
              className={`relative text-left rounded-xl border transition-all duration-200 cursor-pointer overflow-hidden ${
                isSelected
                  ? 'border-[#4B6CF7]/40 bg-[#4B6CF7]/[0.07]'
                  : 'border-white/[0.06] bg-[#111112] hover:border-white/10 hover:bg-white/[0.02]'
              }`}
            >
              {/* Mobile: horizontal layout / Desktop: vertical layout */}
              <div className="flex sm:flex-col gap-4 p-4 sm:p-5">
                {/* Icon */}
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg shrink-0 transition-colors ${
                  isSelected ? 'bg-[#4B6CF7]/15 border border-[#4B6CF7]/20' : 'bg-white/[0.04]'
                }`}>
                  <i className={`${company.icon} text-lg ${isSelected ? 'text-[#7A9BFF]' : 'text-[#555555]'}`}></i>
                </div>

                <div className="flex-1 min-w-0">
                  {/* Label */}
                  <div className={`text-sm font-semibold mb-0.5 leading-snug transition-colors ${
                    isSelected ? 'text-[#F2F2F2]' : 'text-[#999999]'
                  }`}>
                    {company.label}
                  </div>
                  <div className="text-[#2A2A2A] text-[9px] uppercase tracking-widest mb-2 sm:mb-3">
                    {company.labelEn}
                  </div>

                  {/* NonProfit badge */}
                  {company.isNonProfit && (
                    <div className={`inline-flex items-center gap-1 text-[9px] px-2 py-0.5 rounded-full border mb-2 sm:mb-3 ${
                      isSelected ? 'text-[#7A9BFF] border-[#4B6CF7]/30 bg-[#4B6CF7]/10' : 'text-[#333333] border-white/[0.05]'
                    }`}>
                      <i className="ri-heart-line text-[9px]"></i>비영리
                    </div>
                  )}

                  {/* Description */}
                  <p className={`text-[11px] leading-relaxed mb-3 transition-colors ${
                    isSelected ? 'text-[#666666]' : 'text-[#333333]'
                  }`}>
                    {company.description}
                  </p>

                  {/* Features — hidden on mobile to save space */}
                  <div className="hidden sm:block space-y-1.5 mb-3">
                    {company.features.map((f) => (
                      <div key={f} className="flex items-start gap-1.5">
                        <div className={`w-1 h-1 rounded-full mt-1.5 shrink-0 ${
                          isSelected ? 'bg-[#4B6CF7]/60' : 'bg-white/10'
                        }`} />
                        <span className={`text-[10px] leading-relaxed ${
                          isSelected ? 'text-[#555555]' : 'text-[#2A2A2A]'
                        }`}>{f}</span>
                      </div>
                    ))}
                  </div>

                  {/* KPIs */}
                  <div className={`pt-2.5 sm:pt-3 border-t ${isSelected ? 'border-white/[0.08]' : 'border-white/[0.03]'} space-y-1`}>
                    <div className={`text-[9px] uppercase tracking-widest mb-1 ${
                      isSelected ? 'text-[#3D3D3D]' : 'text-[#222222]'
                    }`}>핵심 KPI</div>
                    {company.kpis.slice(0, 2).map((kpi) => (
                      <div key={kpi.label} className="flex items-center gap-1">
                        <i className={`ri-bar-chart-line text-[9px] ${isSelected ? 'text-[#4B6CF7]/50' : 'text-[#222222]'}`}></i>
                        <span className={`text-[10px] ${isSelected ? 'text-[#444444]' : 'text-[#222222]'}`}>{kpi.label}</span>
                      </div>
                    ))}
                    <div className={`text-[10px] mt-1 ${isSelected ? 'text-[#888888]' : 'text-[#2A2A2A]'}`}>
                      초기 자본: {company.capital}
                    </div>
                  </div>
                </div>
              </div>

              {/* Selected indicator */}
              {isSelected && (
                <div className="absolute top-3 right-3 w-5 h-5 flex items-center justify-center rounded-full bg-[#4B6CF7]/20 border border-[#4B6CF7]/30">
                  <i className="ri-check-line text-[#7A9BFF] text-xs"></i>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected Summary */}
      {selected && (
        <div className="w-full max-w-5xl mb-5 bg-[#111112] border border-white/[0.06] rounded-xl p-4 flex items-center gap-4">
          <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#4B6CF7]/10 border border-[#4B6CF7]/20 shrink-0">
            <i className={`${COMPANY_CONFIGS[selected].icon} text-[#7A9BFF] text-base`}></i>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[#F2F2F2] text-sm font-semibold truncate">
              {COMPANY_CONFIGS[selected].label}으로 시작합니다
            </div>
            <div className="text-[#444444] text-xs mt-0.5">직무 5가지 중 하나를 맡아 역할을 체험합니다</div>
          </div>
          <div className="text-right shrink-0 hidden sm:block">
            <div className="text-[#2A2A2A] text-[10px]">초기 자본</div>
            <div className="text-[#666666] text-xs font-medium">{COMPANY_CONFIGS[selected].capital}</div>
          </div>
        </div>
      )}

      {/* CTA */}
      <button
        type="button"
        onClick={handleConfirm}
        disabled={!selected || loading}
        className={`w-full sm:w-auto px-8 md:px-10 py-3 rounded-lg font-semibold text-sm transition-all whitespace-nowrap max-w-xs sm:max-w-none ${
          selected
            ? 'bg-[#4B6CF7] text-white hover:bg-[#5B7CF7] cursor-pointer'
            : 'bg-white/[0.04] text-[#333333] cursor-not-allowed'
        }`}
      >
        {loading ? '설정 중...' : selected ? `${COMPANY_CONFIGS[selected].label} 선택 → 직무 배정` : '기업 유형을 선택해주세요'}
      </button>

      <button
        type="button"
        onClick={() => navigate('/mode-select')}
        className="mt-4 text-[#333333] text-xs hover:text-[#555555] transition-colors cursor-pointer whitespace-nowrap"
      >
        ← 플레이 모드 다시 선택
      </button>
    </div>
  );
}
